// src/services/staffConfirmation/bindStaffConfirmation.ts - swap a confirmation
// handle for a staff confirmation bound to the exact request being sent.
//
// Installed as the LAST request interceptor on every axios client that can carry
// a step-up (the shared instance, the money-op client, the treasury and DPOS
// clients). For a request whose `X-2FA-Code` holds a handle it:
//   1. builds the request as it will leave: METHOD, the absolute URL with its
//      query ordered (requestHash.canonicalPath), the RFC 8785 body and any batch
//      ids;
//   2. hashes it and asks the attempt for a confirmation of that hash;
//   3. rewrites the request to exactly what was hashed (the ordered URL, the
//      canonical body string, no `params`), drops `X-2FA-Code` and sets
//      `X-Staff-Confirmation`.
// Nothing touches the request after this, so the api re-derives the same hash.
// Every other request passes through unchanged.
import type { AxiosInstance, InternalAxiosRequestConfig } from "axios";
import { confirmationHeaders, STEP_UP_HEADER } from "../stepUp";
import {
  containsStaffConfirmationHandle,
  isStaffConfirmationHandle,
  isStaffConfirmationHandleCancelled,
  releaseStaffConfirmationHandle,
  takeStaffConfirmationAttempt,
} from "./confirmationHandles";
import {
  canonicalPath,
  canonicalRequestBody,
  staffRequestHash,
  UnconfirmableBodyError,
} from "./requestHash";
import {
  isStaffConfirmationError,
  StaffConfirmationError,
} from "./staffConfirmationErrors";

declare module "axios" {
  interface AxiosRequestConfig {
    /**
     * The ids of every record a bulk request acts on, for the staff request
     * hash. Leave unset for a request that acts on one record named in its path.
     */
    staffConfirmationBatchIds?: readonly string[];
  }
}

type UriBuilder = Pick<AxiosInstance, "getUri">;

const safeJson = (value: unknown): string => {
  try {
    return JSON.stringify(value) ?? "";
  } catch {
    return "";
  }
};

/** The handle may ride only in the step-up header, never a body or a query. */
const leaksHandle = (config: InternalAxiosRequestConfig): boolean => {
  const data =
    typeof config.data === "string" ? config.data : safeJson(config.data);
  return (
    containsStaffConfirmationHandle(data) ||
    containsStaffConfirmationHandle(safeJson(config.params)) ||
    containsStaffConfirmationHandle(String(config.url ?? ""))
  );
};

export async function bindStaffConfirmation(
  config: InternalAxiosRequestConfig,
  client: UriBuilder
): Promise<InternalAxiosRequestConfig> {
  const handle = config.headers?.get?.(STEP_UP_HEADER);
  if (!isStaffConfirmationHandle(handle)) return config;

  // Take the attempt first: whatever happens next, this handle is spent.
  const attempt = takeStaffConfirmationAttempt(handle);
  try {
    if (leaksHandle(config))
      throw new StaffConfirmationError("CONFIRMATION_FAILED");

    let body: string;
    try {
      body = canonicalRequestBody(config.data);
    } catch (error) {
      if (error instanceof UnconfirmableBodyError)
        throw new StaffConfirmationError("BODY_NOT_CONFIRMABLE");
      throw error;
    }

    const url = new URL(client.getUri(config), window.location.origin);
    const path = canonicalPath(url.pathname, url.search);
    const requestHash = await staffRequestHash({
      method: config.method ?? "get",
      path,
      body,
      batchIds: config.staffConfirmationBatchIds,
    });

    const { confirmation } = await attempt.confirm({ requestHash });
    // The operator closed the dialog while Google was open: send nothing.
    if (isStaffConfirmationHandleCancelled(handle))
      throw new StaffConfirmationError("CONFIRMATION_CANCELLED");

    // Send exactly what was hashed. An absolute URL is used as-is by axios, and
    // the query already lives in it, so `params` must not add another.
    config.url = `${url.origin}${path}`;
    config.params = undefined;
    config.data = body === "" ? undefined : body;
    if (body !== "") config.headers.set("Content-Type", "application/json");
    const confirmationHeader = confirmationHeaders(confirmation);
    if (!confirmationHeader)
      throw new StaffConfirmationError("CONFIRMATION_FAILED");
    config.headers.delete(STEP_UP_HEADER);
    Object.entries(confirmationHeader).forEach(([name, value]) =>
      config.headers.set(name, value)
    );
    return config;
  } catch (error) {
    attempt.cancel();
    throw isStaffConfirmationError(error)
      ? error
      : new StaffConfirmationError("CONFIRMATION_FAILED");
  } finally {
    // Nothing else may use it, whatever happened.
    releaseStaffConfirmationHandle(handle);
  }
}

/**
 * Put the binder on a client. Call it BEFORE any other request interceptor is
 * added: axios runs request interceptors last-added first, so the first one
 * added runs last, after every other interceptor has finished with the URL.
 */
export const installStaffConfirmation = (client: AxiosInstance): void => {
  // Optional chaining as the other install helpers do: a test that stubs axios
  // may hand back a client with no interceptors.
  client?.interceptors?.request?.use?.((config) =>
    bindStaffConfirmation(config, client)
  );
};
